import apiClient from "@/lib/api-client";
import { useSetupStore } from "@/stores/setup-store";

export interface PreviewSeed { key: string; s2Id: string; doi: string | null }

/** Wait this long for the preview before loading lanes in the selector's order. */
export const LANE_PREVIEW_DEADLINE_MS = 20_000;

const inflight = new Set<string>();

/** Test hook. */
export function __resetLanePreview() { inflight.clear(); }

/** Which suggestions lead to papers on the question (backend setup_lane_preview).
 *  Never rejects: any failure records null for the seeds sent, i.e. "keep the
 *  selector's order", so lanes never wait on a broken preview. */
export async function ensureLanePreview(conceptId: string, qh: string, seeds: PreviewSeed[]): Promise<void> {
  const done = useSetupStore.getState().lanePreview[qh] ?? {};
  const todo = seeds.filter((s) => !(s.key in done) && !inflight.has(`${qh}|${s.key}`)).slice(0, 12);
  if (!todo.length) return;
  for (const s of todo) inflight.add(`${qh}|${s.key}`);
  const scores: Record<string, number | null> = Object.fromEntries(todo.map((s) => [s.key, null]));
  try {
    const res = await apiClient
      .post(`api/concepts/${conceptId}/setup/lane-preview`, {
        json: { seeds: todo.map((s) => ({ s2_id: s.s2Id, doi: s.doi })) }, timeout: 120_000,
      })
      .json<{ results: { s2_id: string; share: number | null }[] }>();
    const byS2 = new Map(res.results.map((r) => [r.s2_id, r.share]));
    for (const s of todo) scores[s.key] = byS2.get(s.s2Id) ?? null;
  } catch {
    // null for all: the selector's order
  } finally {
    for (const s of todo) inflight.delete(`${qh}|${s.key}`);
    useSetupStore.getState().setLanePreview(qh, scores);
  }
}

/** The suggestions that load lanes without a click: highest preview share
 *  first, the selector's order breaking ties; unscored ones only fill what's
 *  left, in the selector's order. `keys` is in the selector's order. */
export function pickAutoLanes(keys: string[], scores: Record<string, number | null>, n: number): string[] {
  const scored = keys.filter((k) => scores[k] != null).sort((a, b) => scores[b]! - scores[a]!);
  const rest = keys.filter((k) => scores[k] == null);
  return [...scored, ...rest].slice(0, n);
}
